import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { selectCanAccess, selectCurrentUserId, selectCanDo } from '../app/auth.js'

import { Navigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import { projectsApi, createProject, setProjects, selectProjects, selectProjectId } from '../app/projects.js'
import ProjectCard from '../Components/ProjectCard'
import { usersApi, setUsers, selectUsers } from '../app/users.js'
import { MenuItem, Select } from '@mui/material';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';

const OUTPUTFIELDS = {
  projects: `id, name, userIds, adminIds, ownerIds, projectNumber,missions{id, name, inspections{id, name, url, views{id}, layers{type}}, potreeLayers,{id, name}},offices{id,name,userIds,projectId,missionIds,roles}`,
}

/**
 * Project name becomes the S3 path: spaces → "_", "-" kept as-is.
 * Only letters, digits, spaces and "-" are allowed (_ is reserved for spaces).
 */
function isInvalidProjectName(value) {
  const allowed = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 -';
  return [...value].some((char) => !allowed.includes(char));
}

function ProjectsPage() {
  const canDo = useSelector(selectCanDo)
  const dispatch = useDispatch()
  const userId = useSelector(selectCurrentUserId)
  const projects = useSelector(selectProjects)
  const users = useSelector(selectUsers)

  const nameRef = useRef(null);
  const [owner, setOwner] = useState('');
  const [admins, setAdmins] = useState([]);
  const [projectNumber, setProjectNumber] = useState('');
  const [adminsSelectOpen, setAdminsSelectOpen] = useState(false);
  const [projectFilter, setProjectFilter] = useState('');
  const [nameError, setNameError] = useState(false);
  
  // OPTIMIZATION: Memoize canCreateProject to avoid recalculating on every render
  const canCreateProject = useMemo(() => {
    return canDo("createProject");
  }, [canDo]);
  
  // OPTIMIZATION: Pre-compute filter for fast matching
  // Instead of filtering the array, we'll hide/show components with CSS
  const filterLower = useMemo(() => {
    return projectFilter && projectFilter.length >= 3 ? projectFilter.toLowerCase() : null;
  }, [projectFilter]);
  
  // OPTIMIZATION: Create a Set of matching project IDs for O(1) lookup
  const matchingProjectIds = useMemo(() => {
    if (!filterLower) {
      return null; // null means show all
    }
    const matching = new Set();
    projects.forEach((project) => {
      if (project.name?.toLowerCase().includes(filterLower)) {
        matching.add(project.id);
      }
    });
    return matching;
  }, [filterLower, projects]);
  

  
  function handleCreateProject() {
    const name = nameRef.current?.value ?? '';
    if (isInvalidProjectName(name)) {
      setNameError(true);
      return;
    }
    setNameError(false);
    projectsApi.create({ name, ownerIds: [owner], adminIds: admins, projectNumber }, OUTPUTFIELDS.projects, (result) => {
      dispatch(createProject(result))
    })
  }

  useEffect(() => {
    projectsApi.index({ userId }, OUTPUTFIELDS.projects, (result) => {
      dispatch(setProjects(result))
      const p = result.projects.filter((p) => p.adminIds.includes(userId))

      if (p.length > 0) {
        usersApi.index({ projectId: p[0].id }, "id,name,email", (users) => {
          dispatch(setUsers({ users }))
        })
      } else {
        usersApi.index({}, "id,name,email", (users) => {
          dispatch(setUsers({ users }))
        })
      }
    })
  }, [])

  

  return <div>
    {canCreateProject &&
      <Container sx={{ mt: 4 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h1" variant="h6" color="primary" gutterBottom>
            Add a project
          </Typography>
          <Stack direction="column" spacing={2}>
            <FormControl fullWidth>
              <TextField
                label="Project Name"
                variant="outlined"
                inputRef={nameRef}
                defaultValue=""
                error={nameError}
                helperText={
                  nameError
                    ? 'Only letters, digits, spaces and "-" are allowed (accents / special chars break the S3 path)'
                    : ''
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                label="Numéro d'affaire"
                variant="outlined"
                value={projectNumber}
                onChange={(event) => setProjectNumber(event.target.value)}
              />
            </FormControl>

            {/* Select Owner */}
            <FormControl fullWidth>
              <Select
                displayEmpty
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return <span>Select an owner</span>;
                  const selectedUser = users.find((u) => u.id === selected);
                  return selectedUser?.email || '';
                }}
              >
                <MenuItem disabled value="">
                  Select an owner
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Select Admins */}
            <FormControl fullWidth>
              <Select
                displayEmpty
                multiple
                open={adminsSelectOpen}
                onClose={() => setAdminsSelectOpen(false)}
                onOpen={() => setAdminsSelectOpen(true)}
                value={admins}
                onChange={(e) => {
                  setAdmins(e.target.value || []);
                  setAdminsSelectOpen(false);
                }}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <span>Select administrators</span>;
                  }
                  const emails = selected.map((id) => {
                    const user = users.find((u) => u.id === id);
                    return user?.email || id;
                  });
                  return emails.join(', ');
                }}
              >
                <ListSubheader>Select administrators</ListSubheader>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


            {/* Chips list below the select */}
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {admins.map((id) => {
                const user = users.find((u) => u.id === id);
                return (
                  <Chip
                    key={id}
                    label={user?.email || ""}
                    onDelete={() =>
                      setAdmins((prev) => prev.filter((a) => a !== id))
                    }
                  />
                );
              })}
            </Box>

            <Button
              variant="contained"
              onClick={handleCreateProject}
            >
              Create
            </Button>
          </Stack>
        </Paper>
      </Container>
    }

    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Stack spacing={3} sx={{ flexGrow: 1 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <TextField
            label="Filter projects by name"
            variant="outlined"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            helperText={projectFilter.length > 0 && projectFilter.length < 3 ? "Minimum 3 characters" : ""}
            error={projectFilter.length > 0 && projectFilter.length < 3}
          />
        </FormControl>
        {projects.length > 0 ? (
          projects
            .filter((project) => {
              // Only render visible projects to improve initial load performance
              return matchingProjectIds === null || matchingProjectIds.has(project.id);
            })
            .map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))
        ) : (
          <Typography variant="body1" color="text.secondary">
            Projects loading or no projects found
          </Typography>
        )}
      </Stack>
    </Container>
  </div>
}

export default ProjectsPage
